import React, { useRef, useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

// Configurazione dei due tipi di media inseribili dal NAS.
const MEDIA = {
  video: {
    titolo: 'Inserisci un video',
    listUrl: '/kb/nas/videos',
    uploadUrl: '/kb/nas/upload',
    field: 'video',
    accept: 'video/*',
    uploadLabel: 'Carica un nuovo video',
    hint: 'MP4, MOV, WEBM',
    empty: 'Ancora nessun video. Caricane uno qui sopra.',
    icona: '▶',
    // il video non arriva con embedHtml nella lista → lo costruisco dallo streamUrl
    htmlDaItem: (v) => {
      const t = String(v.name || 'Video').replace(/"/g, '');
      return `<div class="kb-video"><video controls preload="metadata" src="${v.streamUrl}" title="${t}" style="width:100%;height:100%"></video></div>`;
    }
  },
  file: {
    titolo: 'Inserisci un file',
    listUrl: '/kb/nas/files',
    uploadUrl: '/kb/nas/file/upload',
    field: 'file',
    accept: '',
    uploadLabel: 'Carica un nuovo file',
    hint: 'PDF, Word, Excel, immagini, ZIP…',
    empty: 'Ancora nessun file. Caricane uno qui sopra.',
    icona: '📎',
    // il file arriva già con l'HTML dell'allegato pronto dal backend
    htmlDaItem: (f) => f.embedHtml
  }
};

// Editor HTML leggero (dependency-free): WYSIWYG via contentEditable + vista sorgente + media dal NAS.
export default function EditorHtml({ value, onChange }) {
  const ref = useRef();
  const savedRange = useRef(null);
  const fileInputRef = useRef();
  const [mode, setMode] = useState('visuale'); // visuale | html
  const [source, setSource] = useState(value || '');
  // Picker media NAS (condiviso tra video e file)
  const [picker, setPicker] = useState(null); // null | 'video' | 'file'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const cfg = picker ? MEDIA[picker] : null;

  // inizializza il contenuto una sola volta all'ingresso in modalità visuale
  useEffect(() => {
    if (mode === 'visuale' && ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, [mode]); // eslint-disable-line

  const emit = useCallback(() => { if (ref.current) onChange(ref.current.innerHTML); }, [onChange]);

  const salvaSelezione = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0);
    }
  };

  const ripristinaSelezione = () => {
    ref.current && ref.current.focus();
    const sel = window.getSelection();
    if (savedRange.current) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
  };

  const exec = (cmd, val = null) => { ref.current && ref.current.focus(); document.execCommand(cmd, false, val); emit(); };

  const inserisciHtml = (html) => {
    if (mode === 'html') { setSource((s) => { const n = (s || '') + '\n' + html; onChange(n); return n; }); return; }
    ripristinaSelezione();
    document.execCommand('insertHTML', false, html);
    emit();
  };

  const toggleMode = () => {
    if (mode === 'visuale') { setSource(ref.current ? ref.current.innerHTML : ''); setMode('html'); }
    else { setMode('visuale'); setTimeout(() => { if (ref.current) { ref.current.innerHTML = source || ''; emit(); } }, 0); }
  };

  // --- Media dal NAS (video o file) ---
  const apriPicker = async (kind) => {
    salvaSelezione();
    setPicker(kind);
    setItems([]);
    setLoading(true);
    try {
      const { data } = await api.get(MEDIA[kind].listUrl);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Impossibile leggere i contenuti dal NAS');
    } finally {
      setLoading(false);
    }
  };

  const caricaMedia = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !cfg) return;
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append(cfg.field, file);
      fd.append('nome', file.name.replace(/\.[^.]+$/, ''));
      const { data } = await api.post(cfg.uploadUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
        onUploadProgress: (ev) => { if (ev.total) setUploadPct(Math.round((ev.loaded / ev.total) * 100)); }
      });
      inserisciHtml(data.embedHtml);
      toast.success(picker === 'video' ? 'Video caricato e inserito' : 'File caricato e inserito');
      setPicker(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Non sono riuscito a caricare il contenuto');
    } finally {
      setUploadPct(null);
    }
  };

  const scegliItem = (item) => {
    if (!cfg) return;
    inserisciHtml(cfg.htmlDaItem(item));
    setPicker(null);
  };

  const inserisciLink = () => { const url = window.prompt('URL del link:'); if (url) exec('createLink', url); };
  const inserisciImmagine = () => { const url = window.prompt('URL immagine:'); if (url) exec('insertImage', url); };

  const Btn = ({ onClick, title, children }) => (
    <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className="px-2 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg min-w-[32px] transition-colors">{children}</button>
  );

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50/80">
        <Btn onClick={() => exec('bold')} title="Grassetto"><b>B</b></Btn>
        <Btn onClick={() => exec('italic')} title="Corsivo"><i>I</i></Btn>
        <Btn onClick={() => exec('underline')} title="Sottolineato"><u>U</u></Btn>
        <span className="w-px h-5 bg-gray-200 mx-1.5" />
        <Btn onClick={() => exec('formatBlock', 'H2')} title="Titolo"><span className="font-semibold">H2</span></Btn>
        <Btn onClick={() => exec('formatBlock', 'H3')} title="Sottotitolo"><span className="font-semibold">H3</span></Btn>
        <Btn onClick={() => exec('formatBlock', 'P')} title="Paragrafo">¶</Btn>
        <span className="w-px h-5 bg-gray-200 mx-1.5" />
        <Btn onClick={() => exec('insertUnorderedList')} title="Elenco puntato">•</Btn>
        <Btn onClick={() => exec('insertOrderedList')} title="Elenco numerato">1.</Btn>
        <Btn onClick={() => exec('formatBlock', 'BLOCKQUOTE')} title="Citazione">❝</Btn>
        <span className="w-px h-5 bg-gray-200 mx-1.5" />
        <Btn onClick={inserisciLink} title="Inserisci link">🔗</Btn>
        <Btn onClick={inserisciImmagine} title="Immagine da URL">🖼️</Btn>
        <span className="w-px h-5 bg-gray-200 mx-1.5" />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => apriPicker('video')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors shadow-sm shadow-blue-500/20">
          <span className="text-[11px]">▶</span> Video
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => apriPicker('file')}
          className="inline-flex items-center gap-1.5 ml-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg font-medium transition-colors">
          <span className="text-[13px]">📎</span> File
        </button>
        <div className="ml-auto">
          <button type="button" onClick={toggleMode}
            className={`px-2.5 py-1.5 text-xs rounded-lg font-medium transition-colors ${mode === 'html' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}>
            {mode === 'html' ? '◀ Editor' : '</> HTML'}
          </button>
        </div>
      </div>

      {/* Corpo */}
      {mode === 'visuale' ? (
        <div ref={ref} contentEditable suppressContentEditableWarning
          onInput={emit} onBlur={() => { salvaSelezione(); emit(); }} onKeyUp={salvaSelezione} onMouseUp={salvaSelezione}
          className="kb-content min-h-[320px] max-h-[60vh] overflow-y-auto px-5 py-4 focus:outline-none"
          data-placeholder="Scrivi il contenuto dell'articolo…" />
      ) : (
        <textarea value={source} onChange={(e) => { setSource(e.target.value); onChange(e.target.value); }}
          className="w-full min-h-[320px] max-h-[60vh] px-5 py-4 font-mono text-xs text-gray-800 focus:outline-none bg-gray-50/50" spellCheck={false} />
      )}

      {/* Picker media (video / file) */}
      {picker && cfg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={() => setPicker(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">{cfg.titolo}</h3>
              <button onClick={() => setPicker(null)} className="text-gray-400 hover:text-gray-700 text-xl leading-none transition-colors">×</button>
            </div>
            <div className="p-6 overflow-y-auto">
              {/* Carica nuovo */}
              <label className={`flex flex-col items-center justify-center gap-1 w-full py-6 mb-5 border-2 border-dashed rounded-xl cursor-pointer text-sm font-medium transition-colors ${uploadPct !== null ? 'border-blue-200 bg-blue-50 text-blue-400' : 'border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50'}`}>
                {uploadPct !== null
                  ? <span>Caricamento… {uploadPct}%</span>
                  : <><span className="text-lg leading-none">⬆</span><span>{cfg.uploadLabel}</span><span className="text-xs font-normal text-gray-400">{cfg.hint}</span></>}
                <input ref={fileInputRef} type="file" accept={cfg.accept || undefined} className="hidden" onChange={caricaMedia} disabled={uploadPct !== null} />
              </label>
              {uploadPct !== null && (
                <div className="h-1.5 bg-blue-100 rounded-full mb-5 overflow-hidden">
                  <div className="h-full bg-blue-600 transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
              )}

              {/* Contenuti esistenti */}
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2.5">Già caricati</p>
              {loading ? (
                <p className="text-sm text-gray-400 py-6 text-center">Caricamento…</p>
              ) : items.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">{cfg.empty}</p>
              ) : (
                <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                  {items.map((it) => (
                    <button key={it.path || it.name} onClick={() => scegliItem(it)}
                      className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-blue-50/50 transition-colors group">
                      <span className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 text-xs">{cfg.icona}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-gray-900 truncate">{(it.name || '').replace(/^\d+_/, '')}</span>
                        <span className="block text-xs text-gray-400">{it.size ? (it.size > 1048576 ? (it.size / 1048576).toFixed(1) + ' MB' : Math.round(it.size / 1024) + ' KB') : ''}</span>
                      </span>
                      <span className="text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">Inserisci →</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
